// @suite/contracts-sumi — Zod shared between backend and frontend for the Proveedores (SUMI) module.
// ZERO Hono/Drizzle/infrastructure imports allowed in this package (contracts-self-contained,
// SL-6.3 / SL-6-B) — @suite/contracts-kernel is the one exception, a fellow contracts package.
//
// Slice 6 (locx-rental-bm): minimal supplier Zod schema for the ISupplierLookup port surface.
// add-sumi-proveedores-abm (2026-08-22): the full ABM screen — CreateSchema/UpdateSchema/
// ResponseSchema grow organizationId + cuit, same shape as @suite/contracts-kernel's
// Client/CreateClientBody/UpdateClientBody (clients.ts) for the identical CUIT-driven
// find-or-create pattern (organization is kernel-owned, shared by every module).

import { z } from 'zod';
import { TaxConditionSchema } from '@suite/contracts-kernel';
import type { PaginatedResponse } from '@suite/contracts-kernel';

// ---------------------------------------------------------------------------
// SupplierRefSchema — minimal supplier projection (SUMI-1.2 / SL-40.2)
// ---------------------------------------------------------------------------
// Matches the SupplierRow interface in servicios domain/ports/ISupplierLookup.ts.
// Only the fields required by the ISupplierLookup port contract are included here.
// Additional supplier fields (e.g. tax_id, contact) belong in the next SUMI change.
// ---------------------------------------------------------------------------

export const SupplierRefSchema = z.object({
  id:     z.string().uuid(),
  name:   z.string().min(1),
  active: z.boolean(),
});

export type SupplierRef = z.infer<typeof SupplierRefSchema>;

// ---------------------------------------------------------------------------
// Minimal supplier self-service (add-compras-requi-v2 follow-on) — a picker/create
// pair so a buyer can invite a supplier to an RFQ without leaving the flow. Full
// SUMI CRUD (contacts, tax id, addresses) is still a future, dedicated change —
// this is deliberately minimal, matching the current sumi.supplier schema.
// ---------------------------------------------------------------------------

/**
 * Supplier ficha contact and bank data (migration 0008) — local to sumi.supplier, never dual-read from
 * identity.organization. `null` clears a field on PATCH; `cbu` holds a 22-digit CBU/CVU or a bank alias
 * (6–20 letters, digits, dots or hyphens, at least one letter — so a mistyped short CBU is never taken for an alias).
 */
const SupplierContactoFields = {
  phone: z.string().trim().min(1).max(50).nullable().optional(),
  email: z.string().trim().email('El correo no es válido.').max(200).nullable().optional(),
  cbu: z
    .string()
    .regex(/^(\d{22}|(?=.*[A-Za-z])[A-Za-z0-9.-]{6,20})$/, 'Ingresá un CBU/CVU de 22 dígitos o un alias de 6 a 20 caracteres.')
    .nullable()
    .optional(),
  // Migration 0015 — fiscal data printed on purchase orders, same local-only posture.
  address: z.string().trim().min(1).max(300).nullable().optional(),
  taxCondition: TaxConditionSchema.nullable().optional(),
};

export const SupplierCreateSchema = z.object({
  name: z.string().min(1).max(200),
  // add-sumi-proveedores-abm: optional CUIT — POST /sumi/suppliers resolves-or-
  // creates the matching identity.organization when present (same flow as
  // POST /clients, clients.routes.ts:272-327). A cuit-less body behaves
  // exactly as before this change.
  // add-cuentas-pagar Phase 1 (SUMI-4.3): the SHAPE here is unchanged (still
  // a raw string) — checksum/format validation runs at the route via the
  // shared kernel validator, not via a Zod refinement in this schema, so
  // both backend and frontend keep parsing the same lenient wire shape and
  // the 422 error always carries the kernel's own message text.
  cuit: z.string().max(32).optional(),
  // add-cuentas-pagar Phase 1 (SUMI-4.1) — optional at alta time; suppliers
  // created without it simply have no default payment term yet.
  paymentTermDefaultDays: z.number().int().nonnegative().optional(),
  ...SupplierContactoFields,
});
export type SupplierCreateInput = z.infer<typeof SupplierCreateSchema>;

// add-sumi-proveedores-abm — PATCH /sumi/suppliers/:id (admin only). Rename
// and/or soft-deactivate, same shape as UpdateClientBodySchema's mutable
// subset. Deliberately does NOT include cuit — re-linking an existing
// supplier to a different organization is out of this change's scope.
export const SupplierUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  active: z.boolean().optional(),
  // add-cuentas-pagar Phase 1 (SUMI-4.1) — local-only field, same posture as
  // `active`: never touches identity.organization, applies even when the
  // supplier is org-linked (sumi.routes.ts PATCH handler).
  paymentTermDefaultDays: z.number().int().nonnegative().optional(),
  ...SupplierContactoFields,
});
export type SupplierUpdateInput = z.infer<typeof SupplierUpdateSchema>;

export const SupplierResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  active: z.boolean(),
  createdAt: z.union([z.string(), z.date()]),
  // add-sumi-proveedores-abm: dual-read, same posture as ClientSchema's
  // cuit/organizationId (contracts-kernel/clients.ts) — when organizationId
  // is set, name/cuit resolve from identity.organization instead of this
  // supplier row's own columns (sumi.routes.ts's toSupplierResponse).
  cuit: z.string().nullable(),
  organizationId: z.string().uuid().nullable(),
  // add-cuentas-pagar Phase 1 (SUMI-4.1) — ALWAYS local, never dual-read from
  // identity.organization (that table has no equivalent column).
  paymentTermDefaultDays: z.number().int().nonnegative().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  cbu: z.string().nullable(),
  address: z.string().nullable(),
  taxCondition: TaxConditionSchema.nullable(),
});
export type SupplierResponse = z.infer<typeof SupplierResponseSchema>;

/** GET /sumi/supplier-categories — every supplier↔rubro assignment of the tenant (buyer supplier pickers). */
export const SupplierCategoryAssignmentsResponseSchema = z.object({
  items: z.array(z.object({ supplierId: z.string().uuid(), categoryId: z.string().uuid(), categoryName: z.string().nullable() })),
});
export type SupplierCategoryAssignmentsResponse = z.infer<typeof SupplierCategoryAssignmentsResponseSchema>;

/** Paginated admin listing response — GET /sumi/suppliers with pagination/search/includeInactive params. */
export type SupplierListResponse = PaginatedResponse<SupplierResponse>;

// ---------------------------------------------------------------------------
// Cuentas a Pagar (AP) section — reorganizar-cuentas-pagar-bajo-sumi: absorbed
// from the retired @suite/contracts-cuentas-pagar package. See
// ./cuentas-pagar/index.ts for the full barrel (closed vocabularies, key
// logic, HTTP DTOs, response contracts).
// ---------------------------------------------------------------------------
export * from './cuentas-pagar/index.js';
